export function toGuarantorEmrRow(guarantor: {
  id?: number | string | null;
  posted_to_emr?: boolean;
  posted_at?: string | Date | null;
  updated_in_emr?: boolean;
  updated_in_emr_by?: string | number | null;
  updated_in_emr_at?: string | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
}) {
  return {
    guarantor_id: guarantor?.id,
    guarantor_posted_to_emr: guarantor?.posted_to_emr,
    guarantor_posted_at: guarantor?.posted_at,
    guarantor_updated_in_emr: guarantor?.updated_in_emr,
    guarantor_updated_in_emr_by: guarantor?.updated_in_emr_by,
    guarantor_updated_in_emr_at: guarantor?.updated_in_emr_at,
    guarantor_record_status: guarantor?.record_status,
    guarantor_modified_at: guarantor?.modified_at
  };
}

function guarantorRecordStatus(row: {
  guarantor_record_status?: string | null;
  record_status?: string | null;
}): string | null | undefined {
  return row?.guarantor_record_status ?? row?.record_status;
}

export function isGuarantorEmrUpdateConfirmed(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
  guarantor_updated_in_emr?: boolean;
  guarantor_updated_in_emr_by?: string | number | null;
  guarantor_updated_in_emr_at?: string | null;
}): boolean {
  if (!row?.guarantor_id) {
    return false;
  }

  return Boolean(
    row?.guarantor_posted_to_emr &&
      row?.guarantor_updated_in_emr &&
      row?.guarantor_updated_in_emr_by != null &&
      String(row.guarantor_updated_in_emr_by).trim() !== '' &&
      row?.guarantor_updated_in_emr_at
  );
}

export function isGuarantorAwaitingEmrPost(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
}): boolean {
  return Boolean(row?.guarantor_id) && !row?.guarantor_posted_to_emr;
}

export function isGuarantorAwaitingEmrUpdate(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
  guarantor_updated_in_emr?: boolean;
}): boolean {
  return (
    Boolean(row?.guarantor_id) &&
    Boolean(row?.guarantor_posted_to_emr) &&
    row?.guarantor_updated_in_emr === false
  );
}

export function canMarkGuarantorPostedInEmr(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
}): boolean {
  return Boolean(row?.guarantor_id) && !row?.guarantor_posted_to_emr;
}

const EDIT_AFTER_POST_MS = 2000;

function isLocalEditAfterEmrPost(
  modifiedAt?: string | Date | null,
  postedAt?: string | Date | null,
  recordStatus?: string | null
): boolean {
  if (recordStatus !== 'updated') {
    return false;
  }
  if (modifiedAt == null || modifiedAt === '' || postedAt == null || postedAt === '') {
    return false;
  }
  const modifiedTime = new Date(modifiedAt).getTime();
  const postedTime = new Date(postedAt).getTime();
  if (Number.isNaN(modifiedTime) || Number.isNaN(postedTime)) {
    return false;
  }
  return modifiedTime - postedTime > EDIT_AFTER_POST_MS;
}

export function canMarkGuarantorUpdatedInEmr(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
  guarantor_updated_in_emr?: boolean;
  guarantor_record_status?: string | null;
  guarantor_modified_at?: string | Date | null;
  guarantor_posted_at?: string | Date | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  const recordStatus = guarantorRecordStatus(row);
  const modifiedAt = row?.guarantor_modified_at ?? row?.modified_at;
  const postedAt = row?.guarantor_posted_at ?? row?.posted_at;
  return (
    Boolean(row?.guarantor_id) &&
    Boolean(row?.guarantor_posted_to_emr) &&
    row?.guarantor_updated_in_emr === false &&
    isLocalEditAfterEmrPost(modifiedAt, postedAt, recordStatus)
  );
}

export function showsGuarantorEmrSyncedChip(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
  guarantor_updated_in_emr?: boolean;
  guarantor_record_status?: string | null;
  guarantor_modified_at?: string | Date | null;
  guarantor_posted_at?: string | Date | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return (
    Boolean(row?.guarantor_posted_to_emr) && !canMarkGuarantorUpdatedInEmr(row)
  );
}

export function showsGuarantorUpdatedEmrDetails(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
  guarantor_updated_in_emr?: boolean;
  guarantor_updated_in_emr_by?: string | number | null;
  guarantor_updated_in_emr_at?: string | null;
  guarantor_record_status?: string | null;
  guarantor_modified_at?: string | Date | null;
  guarantor_posted_at?: string | Date | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  const recordStatus = guarantorRecordStatus(row);
  const modifiedAt = row?.guarantor_modified_at ?? row?.modified_at;
  const postedAt = row?.guarantor_posted_at ?? row?.posted_at;
  return (
    isGuarantorEmrUpdateConfirmed(row) &&
    isLocalEditAfterEmrPost(modifiedAt, postedAt, recordStatus)
  );
}

export function showsGuarantorPostedEmrDetails(row: {
  guarantor_id?: number | string | null;
  guarantor_posted_to_emr?: boolean;
  guarantor_updated_in_emr?: boolean;
  guarantor_updated_in_emr_by?: string | number | null;
  guarantor_updated_in_emr_at?: string | null;
  guarantor_record_status?: string | null;
  guarantor_modified_at?: string | Date | null;
  guarantor_posted_at?: string | Date | null;
  record_status?: string | null;
  modified_at?: string | Date | null;
  posted_at?: string | Date | null;
}): boolean {
  return (
    Boolean(row?.guarantor_posted_to_emr) && !showsGuarantorUpdatedEmrDetails(row)
  );
}
